import React, { JSX, useEffect, useState } from "react";
import { BenchmarkingAddress, BenchmarkingProviderFlyoutData, BenchmarkingProviderContractDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";

interface BenchmarkingProviderFlyoutAddressTableProps {
  benchmarkingProviderFlyoutData: BenchmarkingProviderFlyoutData;
  loading: boolean;
  npi: string;
  formatNetworkList: (networkList: string[]) => JSX.Element;
}

interface BenchmarkingProviderFlyoutAddressTableData {
  associatedAddresses: JSX.Element;
  network: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  associatedAddresses: {
    alignment: TextAlignment.Left,
    displayText: "Associated Addresses",
    noResize: true,
    noSort: true,
    testId: "associated-addresses",
  },
  network: {
    alignment: TextAlignment.Left,
    displayText: "Network",
    noResize: true,
    noSort: true,
    testId: "network",
  },
};

const BenchmarkingProviderFlyoutAddressTable = (props: BenchmarkingProviderFlyoutAddressTableProps): JSX.Element => {
  const [tableData, setTableData] = useState<BenchmarkingProviderFlyoutAddressTableData[]>([]);

  useEffect(() => {
    const formatAddresses = (flyoutData: BenchmarkingProviderContractDetails): JSX.Element => {
      const getAddress = (addresses: BenchmarkingAddress[]): JSX.Element => {
        return (
          <React.Fragment>
            {addresses.map((address, index) => (
              <div className="mb-3" key={index}>
                <div>
                  {address.address}
                </div>
                {address.address2 && (
                  <div>
                    {address.address2}
                  </div>
                )}
                <div>
                  {address.city}, {address.state} {address.zip}
                </div>
              </div>
            ))}
          </React.Fragment>
        );
      }

      // the addresses are to be displayed vertically, in groups of five, with two groups per row
      const groupsOfFive: number = Math.ceil(flyoutData.addresses.length / 5);
      let start = 0;
      let end = 5;

      return (
        <div className="row">
          {[...Array(groupsOfFive)].map((x, index) => {
            const address = getAddress(flyoutData.addresses.slice(start, end));
            start = start + 5;
            end = end + 5;

            return (
              <div className="col-md-6" key={index}>
                {address}
              </div>
            )
          })}
        </div>
      );
    }

    /* eslint-disable sort-keys */
    const newTableData: BenchmarkingProviderFlyoutAddressTableData[] = props.benchmarkingProviderFlyoutData?.contractDetails?.map((x, index) => {
      return {
        [wafflesKeyColumnName]: x.contractId || index.toString(),
        network: <div className="mr-5">{props.formatNetworkList([x.contractId])}</div>,
        associatedAddresses: formatAddresses(x),
      }
    }) || [];
    /* eslint-enable sort-keys */

    setTableData(newTableData);
  }, [props.benchmarkingProviderFlyoutData]);

  return (
    <div className="row">
      <div className="col-12">
        <Waffles<BenchmarkingProviderFlyoutAddressTableData>
          columns={columnConfig}
          data={tableData}
          fullWidth={true}
          loading={props.loading}
          testId={`benchmarking-dashboard--provider-flyout__address-table-${props.npi}`}
          theme={WafflesThemes.Simple}
        />
      </div>
    </div>
  )
}

export default BenchmarkingProviderFlyoutAddressTable;
